/**
 * Custom hooks for optimized data fetching and caching
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiCache } from '../utils/cacheManager';
import enhancedApi from '../utils/enhancedApi';
import { performanceMonitor } from '../utils/performanceMonitor';

/**
 * Hook for fetching data with caching and performance tracking
 * @param {string} url - API endpoint URL
 * @param {Object} options - Options for the fetch
 * @param {Object} options.params - Query parameters
 * @param {number} options.cacheTTL - Cache TTL in ms (0 to disable caching)
 * @param {boolean} options.forceRefresh - Force refresh even if cached
 * @param {any} options.initialData - Initial data to use
 * @param {Array} options.dependencies - Dependencies array for refetching (similar to useEffect)
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useCachedFetch = (url, options = {}) => {
  const {
    params = {},
    cacheTTL = apiCache.defaultTTL,
    forceRefresh = false,
    initialData = null,
    dependencies = []
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track if component is mounted
  const isMounted = useRef(true);
  
  // Create a stable cache key
  const cacheKey = useMemo(() => {
    return `${url}:${JSON.stringify(params)}`;
  }, [url, JSON.stringify(params)]);

  // Content-based signature (avoids varying useEffect dep *array length* from ...spread)
  const depsSignature = JSON.stringify(dependencies ?? []);
  
  // Fetch function that can be called manually
  const fetchData = useCallback(async (skipCache = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await enhancedApi.get(url, {
        params,
        cacheTTL,
        forceRefresh: skipCache || forceRefresh
      });
      
      if (isMounted.current) {
        setData(result);
      }
    } catch (err) {
      console.error(`Error fetching data from ${url}:`, err);
      
      if (isMounted.current) {
        setError(err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [url, JSON.stringify(params), cacheTTL, forceRefresh]);

  // Effect to fetch data on mount and dependency changes
  useEffect(() => {
    // Check if there's cached data first
    const cachedData = apiCache.get(cacheKey);
    
    if (cachedData && !forceRefresh) {
      setData(cachedData);
      setLoading(false);
    } else {
      fetchData();
    }
    
    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, [cacheKey, fetchData, depsSignature]);

  return { data, loading, error, refetch: () => fetchData(true) };
};

/**
 * Hook to memoize expensive calculations with cache support
 * @param {Function} calculationFn - Function to memoize
 * @param {Array} dependencies - Dependencies for recalculation
 * @param {Object} options - Options
 * @param {string} options.cacheKey - Optional explicit cache key
 * @param {number} options.cacheTTL - TTL for cache in ms
 * @returns {any} - The calculated value
 */
export const useCachedCalculation = (calculationFn, dependencies = [], options = {}) => {
  const { 
    cacheKey: explicitCacheKey = null, 
    cacheTTL = 10 * 60 * 1000 
  } = options;

  const depsSignature = JSON.stringify(dependencies ?? []);
  
  // Generate a cache key based on function and deps
  const cacheKey = useMemo(() => {
    if (explicitCacheKey) return explicitCacheKey;
    
    // Not perfect, but a reasonable approach for most cases
    return `calc:${calculationFn.name || 'anonymous'}:${depsSignature}`;
  }, [explicitCacheKey, calculationFn, depsSignature]);
  
  // Return memoized value
  return useMemo(() => {
    performanceMonitor.start(`useCachedCalculation:${cacheKey}`, 'hooks');
    
    try {
      // Check cache
      const cached = apiCache.get(cacheKey);
      if (cached !== null) {
        return cached;
      }
      
      // Calculate
      const result = calculationFn();
      
      // Cache the result
      apiCache.set(cacheKey, result, cacheTTL);
      
      return result;
    } finally {
      performanceMonitor.end(`useCachedCalculation:${cacheKey}`, 'hooks');
    }
  }, [cacheKey, calculationFn, depsSignature]);
};

/**
 * Hook to debounce a function call
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Delay in ms
 * @returns {Function} - Debounced function
 */
export const useDebounce = (fn, delay = 300) => {
  const timeoutRef = useRef(null);
  
  const debouncedFn = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      fn(...args);
    }, delay);
  }, [fn, delay]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return debouncedFn;
};

/**
 * Hook to throttle a function call
 * @param {Function} fn - Function to throttle
 * @param {number} limit - Throttle limit in ms
 * @returns {Function} - Throttled function
 */
export const useThrottle = (fn, limit = 300) => {
  const lastRun = useRef(0);
  const timeoutRef = useRef(null);
  
  const throttledFn = useCallback((...args) => {
    const now = Date.now();
    
    if (now - lastRun.current >= limit) {
      // If it's been longer than our limit, run immediately
      lastRun.current = now;
      return fn(...args);
    }
    
    // Otherwise, schedule to run at the end of the limit period
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      lastRun.current = Date.now();
      fn(...args);
    }, limit);
  }, [fn, limit]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return throttledFn;
};